import React, { useState, useEffect } from "react";
import { Upload, Modal, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";

const getBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const FileUpload = (props) => {
  const {
    value,
    filterType = ["image"],
    btnName = "Upload",
    onChange,
    multiple = false,
    maxCount = 1,
    aspectRatio, // Added aspectRatio prop (e.g., 1 for 1:1)
  } = props;

  const [fileList, setFileList] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");

  const acceptedTypes = [];
  if (filterType.includes("image")) acceptedTypes.push("image/*");
  if (filterType.includes("video")) acceptedTypes.push("video/*");
  if (filterType.includes("file")) acceptedTypes.push("*/*");

  useEffect(() => {
    if (!value) {
      setFileList([]);
      return;
    }

    if (typeof value === "string") {
      setFileList([
        {
          uid: "-1",
          name: value.split("/").pop(),
          status: "done",
          url: value,
        },
      ]);
    } else if (Array.isArray(value)) {
      const mapped = value.map((file, idx) => {
        const fileUrl = file.url || file.thumbUrl;
        return {
          uid: file.uid || `existing-${idx}`,
          name: file.name || fileUrl?.split("/")?.pop() || `file_${idx}`,
          status: file.status || "done",
          url: fileUrl,
          thumbUrl: fileUrl,
          preview: fileUrl,
          originFileObj: file.originFileObj,
        };
      });
      setFileList(mapped);
    }

  }, [value]);

  const beforeUpload = (file) => {
    const { type } = file;
    const fileCategory = type.split("/")[0]; // image, video, etc.

    if (filterType.length) {
      if (
        (filterType.includes("image") && fileCategory === "image") ||
        (filterType.includes("video") && fileCategory === "video") ||
        (filterType.includes("file"))
      ) {
        // valid type
      } else {
        message.error(`Invalid file type. Allowed: ${filterType.join(", ")}`);
        return Upload.LIST_IGNORE;
      }
    }

    if (file.size / 1024 / 1024 > 20) {
      message.error("File must be smaller than 20MB!");
      return Upload.LIST_IGNORE;
    }

    if (aspectRatio && fileCategory === "image") {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
          const img = new Image();
          img.src = event.target.result;
          img.onload = () => {
            const width = img.width;
            const height = img.height;
            const actualRatio = width / height;
            const tolerance = 0.05; // Flexible tolerance for aspect ratio

            if (Math.abs(actualRatio - aspectRatio) > tolerance) {
              const ratioText = aspectRatio === 1 ? "1:1" : aspectRatio;
              message.error(`Image aspect ratio must be ${ratioText}`);
              reject();
            } else {
              resolve();
            }
          };
        };
      });
    }

    return false;
  };

  const handleChange = async ({ fileList: newList }) => {
    const updatedList = await Promise.all(
      newList.map(async (file) => {
        if (file.originFileObj && !file.preview) {
          file.preview = await getBase64(file.originFileObj);
          file.thumbUrl = file.preview;
        }
        return file;
      })
    );
    setFileList(updatedList);
    onChange?.(multiple ? updatedList : updatedList[0]);
  };

  const handlePreview = async (file) => {
    const isImage = file.type?.startsWith("image/") || /\.(png|jpg|jpeg|webp|avif|svg|gif)$/i.test(file.name);
    const isVideo = file.type?.startsWith("video/");

    if (isImage) {
      if (!file.url && !file.preview) {
        file.preview = await getBase64(file.originFileObj);
      }
      setPreviewImage(file.url || file.preview);
      setPreviewOpen(true);
      setPreviewTitle(file.name || file.url.substring(file.url.lastIndexOf("/") + 1));
    } else if (isVideo) {
      if (file.url) window.open(file.url, "_blank");
      else message.info("Preview not available until uploaded.");
    } else {
      if (file.url) window.open(file.url, "_blank");
      else message.info("Preview not available for this file type.");
    }
  };

  return (
    <>
      <Upload
        listType="picture-card"
        fileList={fileList}
        multiple={multiple}
        maxCount={multiple ? maxCount || 5 : 1}
        accept={acceptedTypes.join(",")}
        beforeUpload={beforeUpload}
        onChange={handleChange}
        onPreview={handlePreview}
        showUploadList={{
          showRemoveIcon: true,
          showPreviewIcon: true,
        }}
        customRequest={() => { }}
      >
        {multiple ? (
          <div>
            <PlusOutlined />
            <div>{btnName}</div>
          </div>
        ) : (
          fileList.length < 1 && (
            <div>
              <PlusOutlined />
              <div>{btnName}</div>
            </div>
          )
        )}
      </Upload>

      <Modal
        open={previewOpen}
        title={previewTitle}
        footer={null}
        onCancel={() => setPreviewOpen(false)}
        centered
      >
        <img alt="Preview" src={previewImage} />
      </Modal>
    </>
  );
};

export default FileUpload;
